import { useState } from "react";
import { Box, Button } from "@mui/material";
import { formatSessionTimeRange } from "../../studentSessionHelpers";
import { ROLE_LABEL, categoryColor, plural, sessionLabel, shortDate, shortDow, unavailableReason } from "./scheduleModel";
import { Icon, Note, FactLine, CategoryTile, CategoryLabel, ICONS, outlineBtnSx, TOKENS as T } from "./scheduleUi";

const hrsOf = (o) => (o.durationHours != null ? o.durationHours : 0);
const fmtHrs = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const locationOf = (o) => o.location || (o.sessionType === "VIRTUAL" ? "Online" : "Location TBD");

const primaryBtn = {
  height: 38, px: "16px", borderRadius: "10px", fontSize: 12, fontWeight: 600, letterSpacing: ".2px",
  textTransform: "uppercase", whiteSpace: "nowrap", bgcolor: T.primary, color: "#fff",
  "&:hover": { bgcolor: T.primaryDeep }, "&.Mui-disabled": { opacity: 0.4, color: "#fff", bgcolor: T.primary },
};
const ghostBtn = { ...primaryBtn, bgcolor: "transparent", color: T.muted, "&:hover": { bgcolor: T.press, color: T.ink } };

function ChoiceOption({ pressed, disabled, icon, title, children, bad, onClick }) {
  return (
    <Box component="button" type="button" aria-pressed={pressed} disabled={disabled} onClick={onClick}
      sx={{
        display: "flex", flexDirection: "column", gap: "6px", p: "14px 16px", textAlign: "left", font: "inherit",
        color: "inherit", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
        border: `1px solid ${pressed ? T.violet : T.hairlineCool}`, borderRadius: "10px",
        bgcolor: pressed ? "color-mix(in oklch, var(--color-accent-violet) 5%, #fff)" : "#fff",
        boxShadow: pressed ? `0 0 0 1px ${T.violet}` : "none",
        "&:hover": { borderColor: disabled ? T.hairlineCool : T.ink },
      }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", fontWeight: 600, fontSize: 14 }}>
        <Box sx={{ width: 16, height: 16, borderRadius: "50%", border: `1.5px solid ${pressed ? T.violet : T.hairlineCool}`,
          display: "grid", placeItems: "center", "&::after": pressed ? { content: '""', width: 8, height: 8, borderRadius: "50%", bgcolor: T.violet } : {} }} />
        {icon}{title}
      </Box>
      <Box component="small" sx={{ fontSize: 12.5, color: bad ? T.danger : T.muted, pl: "26px" }}>{children}</Box>
    </Box>
  );
}

function ConfirmBar({ message, detail, sendLabel, busy, onSend, onCancel, sx }) {
  return (
    <Box role="group" aria-label="Confirm request"
      sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", p: "12px 14px", borderRadius: "10px", bgcolor: T.wash, fontSize: 13.5, ...sx }}>
      <Box sx={{ flex: 1, minWidth: 240 }}>
        {message}
        <Box component="small" sx={{ display: "block", fontSize: 12.5, color: T.muted, mt: "2px" }}>{detail}</Box>
      </Box>
      <Button onClick={onSend} disabled={busy} sx={{ ...primaryBtn, height: 32, px: "12px", fontSize: 11 }}>{sendLabel}</Button>
      <Button onClick={onCancel} disabled={busy} sx={{ ...ghostBtn, height: 32, px: "12px", fontSize: 11 }}>Cancel</Button>
    </Box>
  );
}

function SessionRow({ opening, index, picked, reason, onToggle }) {
  const disabled = Boolean(reason);
  return (
    <Box component="label"
      sx={{ display: "grid", gridTemplateColumns: { xs: "24px 80px minmax(0,1fr)", md: "24px 92px minmax(0,1fr) minmax(0,1.2fr)" }, gap: "14px",
        alignItems: "center", p: "11px 14px", fontSize: 13.5, cursor: disabled ? "default" : "pointer",
        bgcolor: disabled ? "#fafafb" : "transparent", "&:hover": { bgcolor: disabled ? "#fafafb" : "#fcfcfd" },
        "& + &": { borderTop: `1px solid ${T.hairline}` } }}>
      <input type="checkbox" checked={picked} disabled={disabled} onChange={onToggle}
        aria-label={`${shortDow(opening.sessionDate)}, ${shortDate(opening.sessionDate)}`}
        style={{ width: 16, height: 16, margin: 0, accentColor: "var(--color-accent-violet)" }} />
      <Box sx={{ opacity: disabled ? 0.6 : 1 }}>
        <Box component="b" sx={{ display: "block", fontWeight: 600 }}>{shortDow(opening.sessionDate)}, {shortDate(opening.sessionDate)}</Box>
        <Box component="small" sx={{ fontSize: 12, color: T.muted }}>{sessionLabel(opening) || `Session ${index + 1}`}</Box>
      </Box>
      <Box sx={{ color: T.muted, opacity: disabled ? 0.6 : 1 }}>
        <Box component="b" sx={{ color: T.ink, fontWeight: 500 }}>{formatSessionTimeRange(opening)}</Box> · {locationOf(opening)}
      </Box>
      <Box sx={{ fontSize: 12.5, display: "flex", alignItems: "center", gap: "6px", color: reason ? T.danger : T.muted, fontWeight: reason ? 500 : 400, gridColumn: { xs: 3, md: "auto" } }}>
        {reason ? <><Icon>{ICONS.alert}</Icon>{reason}</> : "Open"}
      </Box>
    </Box>
  );
}


/**
 * One role of a class with open slots. A single-session role acts directly (the page confirms in a dialog);
 * a multi-session role lets the instructor request the whole class or pick sessions, then confirm inline.
 */
function RoleBlock({ group, conflicts, busy, onAct, onBulk }) {
  const { openings, role, approval } = group;
  const kind = approval ? "signup" : "request";
  const multi = openings.length > 1;
  const [mode, setMode] = useState(null); // "class" | "session"
  const [pick, setPick] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);

  const roleName = ROLE_LABEL[role] || role;
  const hrs = openings.reduce((a, o) => a + hrsOf(o), 0);
  const first = openings[0];
  const last = openings[openings.length - 1];
  const conflicted = openings.filter((o) => unavailableReason(o, conflicts));
  const available = openings.filter((o) => !unavailableReason(o, conflicts));
  const span = multi ? `${shortDate(first.sessionDate)} – ${shortDate(last.sessionDate)}`
    : `${shortDow(first.sessionDate)}, ${shortDate(first.sessionDate)} · ${formatSessionTimeRange(first)}`;
  const places = [...new Set(openings.map(locationOf))].join(", ");
  const wholeBlocked = conflicted.length > 0;
  const inset = { xs: "20px", md: "80px" };

  const reset = () => { setMode(null); setPick(new Set()); setConfirming(false); };
  const choose = (m) => { setMode(m); setConfirming(false); };
  const toggle = (id) => setPick((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allPicked = available.length > 0 && available.every((o) => pick.has(o.id));
  const picked = openings.filter((o) => pick.has(o.id));
  const target = mode === "class" ? openings : picked;
  const send = () => { onBulk(kind, target, mode); reset(); };

  const count = `${plural(openings.length, "session")}${hrs ? ` · ${fmtHrs(hrs)} hrs` : ""}`;
  const summary = multi
    ? <><span>{count}</span> · {span}</>
    : <>{hrs ? `${fmtHrs(hrs)} hrs · ` : ""}{span} · {places}</>;

  return (
    <Box role="group" aria-label={roleName} data-testid={multi ? `class-${group.classId}-${role}` : `slot-${first.id}`}
      sx={{ "& + &": { borderTop: `1px solid ${T.hairline}` } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", p: { xs: "16px 20px 14px", md: "16px 20px 14px 80px" } }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Box component="b" sx={{ display: "block", fontSize: 14.5, fontWeight: 600 }}>{roleName}</Box>
          {!multi && sessionLabel(first) && <Box sx={{ fontSize: 13.5, fontWeight: 500 }}>{sessionLabel(first)}</Box>}
          <Box component="span" sx={{ fontSize: 12.5, color: T.muted }}>{summary}</Box>
          {multi && <Box component="span" sx={{ display: "block", fontSize: 12.5, color: T.muted }}>{places}</Box>}
          {!multi && conflicted.length > 0 && (
            <Box sx={{ mt: "4px", fontSize: 12.5 }}>
              <Note tone="bad" icon={ICONS.alert}>{unavailableReason(first, conflicts)}</Note>
            </Box>
          )}
        </Box>
        {approval && <Box component="span" sx={{ fontSize: 12.5, color: multi ? T.muted : T.warn, fontWeight: multi ? 400 : 600 }}>Approval required</Box>}
        {!multi && (
          <Button onClick={() => onAct({ kind, opening: first })} disabled={busy || Boolean(first.signupFull)} sx={{ ...primaryBtn, height: 32, px: "12px", fontSize: 11 }}>
            {kind === "signup" ? "Sign up" : "Request slot"}
          </Button>
        )}
      </Box>

      {multi && (
        <>
          <Box role="group" aria-label="How do you want to sign up?"
            sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))" }, gap: "12px", p: { xs: "0 20px 16px", md: "0 20px 16px 80px" } }}>
            <ChoiceOption pressed={mode === "class"} disabled={wholeBlocked} bad={wholeBlocked} icon={<Icon>{ICONS.layers}</Icon>} title="Whole class" onClick={() => choose("class")}>
              {wholeBlocked
                ? `Not available: ${plural(conflicted.length, "session")} conflict with your schedule or are full. Choose sessions instead.`
                : `Teach all ${openings.length} sessions as ${roleName.toLowerCase()}. One request is sent for every session.`}
            </ChoiceOption>
            <ChoiceOption pressed={mode === "session"} icon={<Icon>{ICONS.list}</Icon>} title="Session by session" onClick={() => choose("session")}>
              Pick the sessions you can teach. {approval ? "Each one is approved separately." : "You are assigned to each one right away."}
            </ChoiceOption>
          </Box>

          {mode === "session" && (
            <Box role="group" aria-label="Sessions"
              sx={{ ml: inset, mr: "20px", border: `1px solid ${T.hairline}`, borderRadius: "10px", overflow: "hidden" }}>
              <Box component="label" sx={{ display: "flex", alignItems: "center", gap: "14px", p: "11px 14px", bgcolor: "#fafafb", fontWeight: 600, fontSize: 12.5, cursor: "pointer" }}>
                <input type="checkbox" checked={allPicked} onChange={() => setPick(allPicked ? new Set() : new Set(available.map((o) => o.id)))}
                  style={{ width: 16, height: 16, margin: 0, accentColor: "var(--color-accent-violet)" }} />
                <span>Select all available ({available.length})</span>
              </Box>
              {openings.map((o, i) => (
                <SessionRow key={o.id} opening={o} index={i} picked={pick.has(o.id)} reason={unavailableReason(o, conflicts)} onToggle={() => toggle(o.id)} />
              ))}
            </Box>
          )}

          {mode && (confirming ? (
            <ConfirmBar
              message={mode === "class"
                ? `Request all ${openings.length} sessions of ${group.courseTitle} as ${roleName.toLowerCase()}?`
                : `Request ${plural(picked.length, "session")} of ${group.courseTitle} as ${roleName.toLowerCase()}?`}
              detail={approval
                ? "A request goes to the coordinator for each session, so you may be assigned to some and not others."
                : "You are assigned to each session right away."}
              sendLabel="Send request" busy={busy} onSend={send} onCancel={() => setConfirming(false)}
              sx={{ m: { xs: "14px 20px 18px", md: "14px 20px 18px 80px" } }} />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", m: { xs: "14px 20px 18px", md: "14px 20px 18px 80px" }, p: "12px 14px", borderRadius: "10px", bgcolor: T.wash, fontSize: 13.5 }}>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                {mode === "class"
                  ? <><b>Whole class · {plural(openings.length, "session")}</b><Box component="small" sx={{ display: "block", fontSize: 12.5, color: T.muted, mt: "2px" }}>{openings.map((o) => shortDate(o.sessionDate)).join(", ")}</Box></>
                  : picked.length ? <><b>{plural(picked.length, "session")}</b> selected</> : "Select at least one session."}
              </Box>
              <Button onClick={() => setConfirming(true)} disabled={mode === "session" && picked.length === 0} sx={{ ...primaryBtn, height: 32, px: "12px", fontSize: 11 }}>
                {mode === "class" ? "Request whole class" : `Request ${picked.length ? plural(picked.length, "session") : "sessions"}`}
              </Button>
            </Box>
          ))}
          {!mode && <Box sx={{ height: "2px" }} />}
        </>
      )}
    </Box>
  );
}

/**
 * One card per class. The header carries the class facts and one "needed" label per teaching role; the roles
 * (lead, assistant, TA) expand underneath, each with its own sign-up controls.
 */
export default function OpenClassCard({ course, conflicts, busy, onAct, onBulk, onDetails, defaultOpen = false }) {
  const { roles, openings } = course;
  const [open, setOpen] = useState(defaultOpen || roles.length === 1);
  const first = openings[0];
  const last = openings[openings.length - 1];
  const multi = openings.length > 1;
  const solo = roles.length === 1 ? roles[0] : null;
  const anyApproval = roles.some((r) => r.approval);
  const sessionCount = new Set(openings.map((o) => o.sessionId)).size;
  const hrs = [...new Map(openings.map((o) => [o.sessionId, o])).values()].reduce((a, o) => a + hrsOf(o), 0);
  const span = sessionCount > 1 ? `${shortDate(first.sessionDate)} – ${shortDate(last.sessionDate)}`
    : `${shortDow(first.sessionDate)}, ${shortDate(first.sessionDate)} · ${formatSessionTimeRange(first)}`;
  const places = [...new Set(openings.map(locationOf))].join(", ");
  const titleId = `class-${course.key.replace(/\W/g, "-")}`;
  const listId = `${titleId}-roles`;
  const ids = solo && !multi ? [first.sessionId] : null;

  return (
    <Box component="article" aria-labelledby={titleId} sx={cardSx}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "44px minmax(0,1fr)", md: "44px minmax(0,1fr) auto" }, gap: "16px", alignItems: "center", p: "18px 20px" }}>
        <CategoryTile color={categoryColor(course.category)} />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap", mb: "2px" }}>
            <Box component="b" id={titleId} sx={{ fontSize: 16, fontWeight: 600 }}>{course.courseTitle}</Box>
            {course.courseNumber && <Box component="span" sx={{ fontFamily: "var(--font-mono)", fontSize: 12, color: T.muted }}>{course.courseNumber}</Box>}
          </Box>
          <FactLine sx={{ mb: "4px" }}>
            <CategoryLabel category={course.category} />
            {roles.map((r) => <span key={r.role}>{ROLE_LABEL[r.role] || r.role} needed</span>)}
          </FactLine>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "2px 16px", fontSize: 13, color: T.muted }}>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px", "& b": { color: T.ink, fontWeight: 500 } }}>
              <Icon>{ICONS.cal}</Icon><b>{span}</b>
            </Box>
            {sessionCount > 1 && (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <Icon>{ICONS.layers}</Icon>{plural(sessionCount, "session")}{hrs ? ` · ${fmtHrs(hrs)} hrs` : ""}
              </Box>
            )}
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Icon>{ICONS.pin}</Icon>{places}</Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", justifyContent: { xs: "flex-start", md: "flex-end" }, gridColumn: { xs: "1 / -1", md: "auto" } }}>
          {anyApproval && <Box component="span" sx={{ fontSize: 12.5, color: T.muted }}>Approval required</Box>}
          <Button
            onClick={() => onDetails({ classId: course.classId, courseTitle: course.courseTitle, courseNumber: course.courseNumber,
              category: course.category, role: solo ? solo.role : null, sessionIds: ids })}
            aria-label={`Details for ${course.courseTitle}`} sx={outlineBtnSx}>
            Details
          </Button>
          <Button onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls={listId} sx={{ ...outlineBtnSx, gap: "6px" }}>
            {plural(roles.length, "role")}
            <Icon size={14}><path d={open ? "m18 15-6-6-6 6" : "m6 9 6 6 6-6"} /></Icon>
          </Button>
        </Box>
      </Box>
      {open && (
        <Box id={listId} sx={{ borderTop: `1px solid ${T.hairline}` }}>
          {roles.map((g) => (
            <RoleBlock key={g.key} group={g} conflicts={conflicts} busy={busy} onAct={onAct} onBulk={onBulk} />
          ))}
        </Box>
      )}
    </Box>
  );
}

const cardSx = {
  bgcolor: "#fff", border: `1px solid ${T.hairline}`, borderRadius: "16px", boxShadow: "var(--shadow-card-sm)",
  "& + &": { mt: "12px" },
};
